import { useCallback, useLayoutEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableElements(root: HTMLElement): HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => {
        if (element.hasAttribute('disabled') || element.getAttribute('aria-hidden') === 'true') return false;
        return element.tabIndex >= 0;
    });
}

function focusIfConnected(element: HTMLElement | null): void {
    if (element && document.contains(element)) {
        element.focus();
    }
}

/**
 * Job-sheet keyboard shell shared with the accepted and rejected sheets:
 * focus Back on open, Escape runs the sheet close callback, Tab stays inside,
 * and focus returns to the trigger on close or unmount.
 */
export default function useJobSheetDialog(onEscape: () => void) {
    const dialogRef = useRef<HTMLDivElement>(null);
    const backButtonRef = useRef<HTMLButtonElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const closedRef = useRef(false);
    const onEscapeRef = useRef(onEscape);
    onEscapeRef.current = onEscape;

    const restoreFocus = useCallback(() => {
        if (closedRef.current) return;
        closedRef.current = true;
        const previous = previouslyFocusedRef.current;
        previouslyFocusedRef.current = null;
        focusIfConnected(previous);
    }, []);

    useLayoutEffect(() => {
        const active = document.activeElement;
        if (active instanceof HTMLElement && active !== document.body && !dialogRef.current?.contains(active)) {
            previouslyFocusedRef.current = active;
        }
        closedRef.current = false;

        const keepFocusInside = (shiftKey: boolean) => {
            const root = dialogRef.current;
            if (!root || closedRef.current) return;
            const focusable = focusableElements(root);
            if (focusable.length === 0) {
                root.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const activeElement = document.activeElement;
            const inside = activeElement instanceof Node && root.contains(activeElement);
            if (shiftKey) {
                if (!inside || activeElement === first) last.focus();
                return;
            }
            if (!inside || activeElement === last) first.focus();
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if (closedRef.current) return;
            if (event.key === 'Escape') {
                event.preventDefault();
                onEscapeRef.current();
                return;
            }
            if (event.key !== 'Tab') return;
            const root = dialogRef.current;
            if (!root) return;
            const focusable = focusableElements(root);
            const activeElement = document.activeElement;
            const inside = activeElement instanceof Node && root.contains(activeElement);
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const leaving =
                focusable.length === 0 ||
                !inside ||
                (event.shiftKey ? activeElement === first : activeElement === last);
            if (!leaving) return;
            event.preventDefault();
            keepFocusInside(event.shiftKey);
        };

        const onFocusIn = (event: FocusEvent) => {
            if (closedRef.current) return;
            const root = dialogRef.current;
            if (!root) return;
            const target = event.target;
            if (!(target instanceof Node) || root.contains(target)) return;
            const shiftKey =
                event.relatedTarget instanceof Node && root.contains(event.relatedTarget)
                    ? event.relatedTarget === focusableElements(root)[0]
                    : false;
            keepFocusInside(shiftKey);
        };

        document.addEventListener('keydown', onKeyDown, true);
        document.addEventListener('focusin', onFocusIn, true);
        backButtonRef.current?.focus();

        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            document.removeEventListener('focusin', onFocusIn, true);
            if (!closedRef.current) {
                closedRef.current = true;
                const previous = previouslyFocusedRef.current;
                previouslyFocusedRef.current = null;
                focusIfConnected(previous);
            }
        };
    }, []);

    return { dialogRef, backButtonRef, restoreFocus };
}
